import type { CompositeScore } from "@/lib/festival-weather/types";
import { scoreOutOf100 } from "@/lib/conditions/score-display";

// Colour tiers mirror the five band-label tiers in scoring.ts `bandLabel`
// (score <= 2 / <= 4 / <= 6 / <= 8 / else) so the badge colour and the band
// copy never disagree. Boundaries use `>` to match bandLabel's `<=` cut points:
// a score of exactly 8.0 is the band[3] tier, so it reads lime, not emerald.
function badgeTone(score: number): { bg: string; text: string } {
  if (score > 8) return { bg: "bg-emerald-500", text: "text-white" };
  if (score > 6) return { bg: "bg-lime-500", text: "text-white" };
  if (score > 4) return { bg: "bg-amber-500", text: "text-white" };
  if (score > 2) return { bg: "bg-orange-500", text: "text-white" };
  return { bg: "bg-slate-500", text: "text-white" };
}

export function ConditionsScoreBadge({
  score,
  size = "md",
}: {
  score: CompositeScore;
  size?: "sm" | "md" | "lg";
}) {
  const tone = badgeTone(score.total);
  const dims =
    size === "lg"
      ? "px-5 py-3 text-[28px]"
      : size === "sm"
      ? "px-2.5 py-1.5 text-[14px]"
      : "px-4 py-2.5 text-[20px]";
  return (
    <div
      className={`inline-flex items-center gap-3 rounded-[14px] ${tone.bg} ${tone.text} ${dims}`}
    >
      <span className="font-serif font-bold tabular-nums leading-none">
        {scoreOutOf100(score.total)}
      </span>
      <span className="text-[11px] uppercase tracking-[1px] font-semibold opacity-90 border-l border-white/30 pl-3">
        {score.label}
      </span>
    </div>
  );
}
